import React, { useState, useEffect } from 'react';
import Sidebar from '../components/SideBar';
import Header from '../components/Header';
import {
  PublicationFilters,
  PublicationStats,
  PublicationList,
  PublicationDetail
} from '../components/publication';
import useGlobale from '../components/global';
import useMenuItems from '../components/Menu';
import api from '../components/api';
import { toast } from 'react-toastify';
import { RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import Swal from 'sweetalert2';

const PublicationPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user } = useGlobale();
  const { menuItems } = useMenuItems();
  
  // États pour les données
  const [publications, setPublications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [selectedPublication, setSelectedPublication] = useState(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // États pour les filtres
  const [filters, setFilters] = useState({
    search: '',
    est_masquee: '',
    date_debut: '',
    date_fin: '',
    ordering: '-created_at'
  });
  
  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth >= 1024);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Charger les statistiques
  useEffect(() => {
    fetchStats();
  }, []);

  // Charger les publications quand les filtres changent
  useEffect(() => {
    setCurrentPage(1); // Reset à la page 1 lors du changement de filtres
    fetchPublications(1);
  }, [filters]);

  // Charger les publications quand la page change
  useEffect(() => {
    if (currentPage > 1) {
      fetchPublications(currentPage);
    }
  }, [currentPage]);

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const response = await api.get('/api/publication/admin/publications/stats/');
      setStats(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchPublications = async (page = 1) => {
    try {
      setLoading(true);
      
      // Construire les paramètres de requête
      const params = new URLSearchParams();
      params.append('page', page);
      if (filters.search) params.append('search', filters.search);
      if (filters.est_masquee) params.append('est_masquee', filters.est_masquee);
      if (filters.date_debut) params.append('date_debut', filters.date_debut);
      if (filters.date_fin) params.append('date_fin', filters.date_fin);
      if (filters.ordering) params.append('ordering', filters.ordering);
      
      const response = await api.get(`/api/publication/admin/publications/?${params.toString()}`);
      
      // Gérer la réponse paginée
      setPublications(response.data.results || []);
      setTotalCount(response.data.count || 0);
      setTotalPages(Math.ceil((response.data.count || 0) / 12));
    } catch (error) {
      console.error('Erreur lors du chargement des publications:', error);
      toast.error('Erreur lors du chargement des publications');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      est_masquee: '',
      date_debut: '',
      date_fin: '',
      ordering: '-created_at'
    });
  };

  const handleViewPublication = (publication) => {
    setSelectedPublication(publication);
  };

  const handleDeletePublication = async (publication) => {
    const result = await Swal.fire({
      title: 'Supprimer cette publication ?',
      text: "Cette action est irréversible !",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#FF6B00',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
      customClass: {
        popup: 'rounded-xl',
        confirmButton: 'rounded-lg px-6 py-2',
        cancelButton: 'rounded-lg px-6 py-2'
      }
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/api/publication/admin/publications/${publication.id}/`);
        Swal.fire({
          title: 'Supprimée !',
          text: 'La publication a été supprimée avec succès.',
          icon: 'success',
          confirmButtonColor: '#FF6B00',
          confirmButtonText: 'OK',
          timer: 2000,
          customClass: {
            popup: 'rounded-xl',
            confirmButton: 'rounded-lg px-6 py-2'
          }
        });
        fetchPublications(currentPage);
        fetchStats();
        setSelectedPublication(null);
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        Swal.fire({
          title: 'Erreur !',
          text: 'Erreur lors de la suppression de la publication.',
          icon: 'error',
          confirmButtonColor: '#FF6B00',
          confirmButtonText: 'OK',
          customClass: {
            popup: 'rounded-xl',
            confirmButton: 'rounded-lg px-6 py-2'
          }
        });
      }
    }
  };
  const handleDeleteComment = async (comment) => {
    const result = await Swal.fire({
      title: 'Supprimer ce commentaire ?',
      text: "Cette action est irréversible !",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#FF6B00',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
      customClass: {
        popup: 'rounded-xl',
        confirmButton: 'rounded-lg px-6 py-2',
        cancelButton: 'rounded-lg px-6 py-2'
      }
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/api/publication/admin/commentaires/${comment.id}/`);
        Swal.fire({
          title: 'Supprimé !',
          text: 'Le commentaire a été supprimé avec succès.',
          icon: 'success',
          confirmButtonColor: '#FF6B00',
          confirmButtonText: 'OK',
          timer: 2000,
          customClass: {
            popup: 'rounded-xl',
            confirmButton: 'rounded-lg px-6 py-2'
          }
        });
        
        // Recharger la publication pour mettre à jour les commentaires
        const response = await api.get(`/api/publication/admin/publications/${selectedPublication.id}/`);
        setSelectedPublication(response.data);
        fetchPublications(currentPage);
      } catch (error) {
        console.error('Erreur lors de la suppression du commentaire:', error);
        Swal.fire({
          title: 'Erreur !',
          text: 'Erreur lors de la suppression du commentaire.',
          icon: 'error',
          confirmButtonColor: '#FF6B00',
          confirmButtonText: 'OK',
          customClass: {
            popup: 'rounded-xl',
            confirmButton: 'rounded-lg px-6 py-2'
          }
        });
      }
    }
  };

  const handleToggleVisibility = async (publication) => {
    try {
      await api.post(`/api/publication/admin/publications/${publication.id}/toggle-visibility/`);
      toast.success(`Publication ${publication.est_masquee ? 'affichée' : 'masquée'} avec succès`);
      fetchPublications(currentPage);
      fetchStats();
      
      // Mettre à jour la publication sélectionnée si c'est celle-ci
      if (selectedPublication && selectedPublication.id === publication.id) {
        setSelectedPublication({
          ...selectedPublication,
          est_masquee: !selectedPublication.est_masquee
        });
      }
    } catch (error) {
      console.error('Erreur lors du changement de visibilité:', error);
      toast.error('Erreur lors du changement de visibilité');
    }
  };

  const handleRefresh = () => {
    fetchPublications(currentPage);
    fetchStats();
    toast.info('Données actualisées');
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="flex min-h-screen bg-[#F4F8E9]">
      <Sidebar
        isOpen={isSidebarOpen}
        menuItems={menuItems}
        onClose={() => setIsSidebarOpen(false)}
      />

      <div className="flex-1 flex flex-col w-full">
        <Header
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          user={user}
        />

        <main className="flex-1 w-full p-4 lg:p-6 overflow-auto">
          {/* Header avec titre et bouton actualiser */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
                Gestion des Publications
              </h1>
              <p className="text-gray-600 text-sm mt-1">
                Gérez et modérez toutes les publications de la plateforme
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="px-4 py-2 bg-[#FF6B00] text-white rounded-lg hover:bg-[#e55d00] transition-colors flex items-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Actualiser</span>
            </button>
          </div>

          {/* Statistiques */}
          {!statsLoading && stats && <PublicationStats stats={stats} />}

          {/* Filtres */}
          <PublicationFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
          />

          {/* Résultats count */}
          {!loading && publications.length > 0 && (
            <div className="mb-4 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <span className="font-medium text-gray-900">{totalCount}</span> publication{totalCount > 1 ? 's' : ''} au total
                <span className="text-gray-400 mx-2">•</span>
                Page <span className="font-medium text-gray-900">{currentPage}</span> sur <span className="font-medium text-gray-900">{totalPages}</span>
              </div>
            </div>
          )}

          {/* Liste des publications */}
          <PublicationList
            publications={publications}
            loading={loading}
            onView={handleViewPublication}
            onDelete={handleDeletePublication}
            onToggleVisibility={handleToggleVisibility}
          />

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm font-medium"
              >
                <ChevronLeft size={16} />
                Précédent
              </button>

              <div className="flex items-center gap-1">
                {[...Array(totalPages)].map((_, index) => {
                  const pageNum = index + 1;
                  // Afficher seulement quelques pages autour de la page actuelle
                  if (
                    pageNum === 1 ||
                    pageNum === totalPages ||
                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === pageNum
                            ? 'bg-[#FF6B00] text-white'
                            : 'bg-white border border-gray-300 hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (
                    pageNum === currentPage - 2 ||
                    pageNum === currentPage + 2
                  ) {
                    return <span key={pageNum} className="px-2 text-gray-400">...</span>;
                  }
                  return null;
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm font-medium"
              >
                Suivant
                <ChevronRight size={16} />
              </button>
            </div>
          )}

          {/* Modal détail */}
          {selectedPublication && (
            <PublicationDetail
              publication={selectedPublication}
              onClose={() => setSelectedPublication(null)}
              onDeleteComment={handleDeleteComment}
              onDeletePublication={handleDeletePublication}
              onToggleVisibility={handleToggleVisibility}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default PublicationPage;